import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft, ChevronRight, Pencil, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ingestReviewApi,
  type IngestReview,
  type IngestReviewStatus,
} from '@/api/ingestReviewApi'
import { friendlyMessage } from '@/api/http'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { Project } from '@/types/nf'
import { IdentityReviewActions } from './IdentityReviewActions'

const PAGE_SIZE = 25
const STATUS_OPTIONS: Array<IngestReviewStatus | 'all'> = ['pending', 'edited', 'accepted', 'rejected', 'all']
const EDIT_FIELDS = ['level', 'equipment', 'location', 'emotional_state', 'active_conflicts', 'relationships'] as const

function fieldText(value: unknown) {
  if (Array.isArray(value)) return value.join(', ')
  if (value === null || value === undefined) return ''
  return String(value)
}

function statusVariant(status: IngestReviewStatus) {
  if (status === 'accepted') return 'default'
  if (status === 'rejected') return 'destructive'
  if (status === 'edited') return 'secondary'
  return 'outline'
}

function compactAttributes(review: IngestReview) {
  const state = review.edited_payload ?? review.proposed_state
  return ['level', 'location']
    .map((field) => fieldText(state[field]))
    .filter(Boolean)
    .join(' / ')
}

function editInitialValues(review: IngestReview | null) {
  const source = review?.edited_payload ?? review?.proposed_state ?? {}
  return Object.fromEntries(EDIT_FIELDS.map((field) => [field, fieldText(source[field])]))
}

function buildEditedPayload(values: Record<string, string>) {
  const payload: Record<string, unknown> = {}
  for (const field of EDIT_FIELDS) {
    const value = values[field]?.trim()
    if (!value) continue
    payload[field] = field === 'active_conflicts' || field === 'relationships'
      ? value.split(',').map((item) => item.trim()).filter(Boolean)
      : value
  }
  return payload
}

/** simple: 简易档位 —— 隐藏批量接受/拒绝与勾选列, 只保留逐条审核。
 *  批量操作一次影响多行且不可逆, 不该出现在简易档位。 */
export function IngestReviewPanel({ project, simple = false, branchId = 'main' }: { project: Project; simple?: boolean; branchId?: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<IngestReviewStatus | 'all'>('pending')
  const [chapterText, setChapterText] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<IngestReview | null>(null)
  const [editValues, setEditValues] = useState<Record<string, string>>({})

  const chapterNo = chapterText.trim() ? Number(chapterText.trim()) : null
  const reviewsQuery = useQuery({
    queryKey: ['ingest-reviews', project.id, branchId, status, chapterNo, page],
    queryFn: () => ingestReviewApi.list(project.id, { branchId, status, chapterNo, page, limit: PAGE_SIZE }),
  })
  const rows = reviewsQuery.data?.items ?? []
  const settings = useQuery({ queryKey: ['entity-review-settings', project.id, branchId], queryFn: () => ingestReviewApi.settings(project.id, branchId) })
  const modeMutation = useMutation({
    mutationFn: (mode: string) => ingestReviewApi.setMode(project.id, mode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['entity-review-settings', project.id] }),
    onError: (error) => toast.error(friendlyMessage(error, t('ingestReview.actionFailed'))),
  })
  const queryKey = ['ingest-reviews']
  const invalidate = async () => {
    setSelected(new Set())
    await queryClient.invalidateQueries({ queryKey })
    await queryClient.invalidateQueries({ queryKey: ['entity-review-settings', project.id] })
    await queryClient.invalidateQueries({ queryKey: ['characters', project.id] })
  }
  const mutateRow = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'reject' }) =>
      action === 'accept' ? ingestReviewApi.accept(project.id, id) : ingestReviewApi.reject(project.id, id),
    onSuccess: invalidate,
    onError: (error) => toast.error(friendlyMessage(error, t('ingestReview.actionFailed'))),
  })
  const batchMutation = useMutation({
    mutationFn: (action: 'accept' | 'reject') =>
      ingestReviewApi.batch(project.id, { ids: [...selected], action }),
    onSuccess: invalidate,
    onError: (error) => toast.error(friendlyMessage(error, t('ingestReview.batchFailed'))),
  })
  const editMutation = useMutation({
    mutationFn: () => ingestReviewApi.update(project.id, editing!.id, buildEditedPayload(editValues)),
    onSuccess: async () => {
      setEditing(null)
      await invalidate()
    },
    onError: (error) => toast.error(friendlyMessage(error, t('ingestReview.saveFailed'))),
  })

  const allPageSelected = useMemo(
    () => rows.length > 0 && rows.every((row) => selected.has(row.id)),
    [rows, selected],
  )
  const pendingAction = mutateRow.isPending || batchMutation.isPending || editMutation.isPending
  const canNext = rows.length === PAGE_SIZE

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const openEdit = (review: IngestReview) => {
    setEditing(review)
    setEditValues(editInitialValues(review))
  }

  return (
    <div className="space-y-4">
      <Label className="flex items-center gap-3">{t('ingestReview.identity.mode')}
        <select aria-label={t('ingestReview.identity.mode')} className="rounded-md border bg-background p-2" value={settings.data?.entity_review_mode ?? 'review_new'} disabled={modeMutation.isPending || !settings.data} onChange={(event) => modeMutation.mutate(event.target.value)}>
          <option value="review_new">{t('ingestReview.identity.review_new')}</option>
          <option value="review_all">{t('ingestReview.identity.review_all')}</option>
          <option value="auto_accept">{t('ingestReview.identity.auto_accept')}</option>
        </select>
      </Label>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid gap-3 sm:grid-cols-[180px_160px]">
          <div className="space-y-2">
            <Label>{t('ingestReview.filters.status')}</Label>
            <Select value={status} onValueChange={(value) => {
              setStatus(value as IngestReviewStatus | 'all')
              setPage(1)
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((item) => (
                  <SelectItem key={item} value={item}>{t(`ingestReview.status.${item}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ingest-review-chapter">{t('ingestReview.filters.chapter')}</Label>
            <Input
              id="ingest-review-chapter"
              inputMode="numeric"
              value={chapterText}
              onChange={(event) => {
                setChapterText(event.target.value.replace(/[^\d]/g, ''))
                setPage(1)
              }}
              placeholder={t('ingestReview.filters.chapterPlaceholder')}
            />
          </div>
        </div>
        {simple ? null : (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={selected.size === 0 || pendingAction}
              onClick={() => batchMutation.mutate('accept')}
            >
              <Check className="size-4" />
              {t('ingestReview.batchAccept')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={selected.size === 0 || pendingAction}
              onClick={() => batchMutation.mutate('reject')}
            >
              <X className="size-4" />
              {t('ingestReview.batchReject')}
            </Button>
          </div>
        )}
      </div>

      {reviewsQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 rounded-md" />
          <Skeleton className="h-44 rounded-md" />
        </div>
      ) : reviewsQuery.error ? (
        <div className="rounded-md border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
          {friendlyMessage(reviewsQuery.error, t('ingestReview.loadFailed'))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {simple ? null : (
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label={t('ingestReview.selectPage')}
                    checked={allPageSelected}
                    onChange={(event) => {
                      const checked = event.target.checked
                      setSelected((prev) => {
                        const next = new Set(prev)
                        for (const row of rows) {
                          if (checked) next.add(row.id)
                          else next.delete(row.id)
                        }
                        return next
                      })
                    }}
                  />
                </TableHead>
              )}
              <TableHead>{t('ingestReview.columns.chapter')}</TableHead>
              <TableHead>{t('ingestReview.columns.name')}</TableHead>
              <TableHead>{t('ingestReview.columns.aliases')}</TableHead>
              <TableHead>{t('ingestReview.columns.attributes')}</TableHead>
              <TableHead>{t('ingestReview.columns.evidence')}</TableHead>
              <TableHead>{t('ingestReview.columns.status')}</TableHead>
              <TableHead className="text-right">{t('ingestReview.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={simple ? 7 : 8} className="h-28 text-center text-muted-foreground">
                  {t('ingestReview.empty')}
                </TableCell>
              </TableRow>
            ) : rows.map((review) => {
              const evidence = review.evidence[0]
              return (
                <TableRow key={review.id}>
                  {simple ? null : (
                    <TableCell>
                      <input
                        type="checkbox"
                        aria-label={t('ingestReview.selectRow', { name: review.canonical_name })}
                        checked={selected.has(review.id)}
                        onChange={(event) => toggle(review.id, event.target.checked)}
                      />
                    </TableCell>
                  )}
                  <TableCell className="nf-mono">{review.chapter_no}</TableCell>
                  <TableCell className="font-medium">{review.canonical_name}</TableCell>
                  <TableCell className="max-w-48 truncate">{review.surface_forms.join(', ')}</TableCell>
                  <TableCell className="max-w-56 truncate">{review.proposed_state.reason ? t(`ingestReview.identity.${String(review.proposed_state.reason)}`) : compactAttributes(review)} {review.proposed_state.candidate_type ? t(`ingestReview.identity.${String(review.proposed_state.candidate_type)}`) : ''}</TableCell>
                  <TableCell className="max-w-72 truncate" title={evidence?.snippet ?? ''}>
                    {evidence?.match_type === 'exact' || evidence?.match_type === 'approximate' ? (
                      <Badge variant="outline" className="mr-1">{t(`ingestReview.evidenceMatch.${evidence.match_type}`)}</Badge>
                    ) : null}
                    {evidence?.snippet ?? ''}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(review.status)}>{t(`ingestReview.status.${review.status}`)}</Badge>
                  </TableCell>
                  <TableCell>
                    {typeof review.proposed_state.reason === 'string' && (review.status === 'pending' || review.status === 'edited') ? <IdentityReviewActions
                      reason={review.proposed_state.reason}
                      initialTarget={typeof review.proposed_state.existing_id === 'string' ? review.proposed_state.existing_id : ''}
                      disabled={pendingAction}
                      onDecision={async (decision, target_id) => {
                        try {
                          await ingestReviewApi.update(project.id, review.id, { decision, target_id })
                          await ingestReviewApi.accept(project.id, review.id)
                          await invalidate()
                        } catch (error) { toast.error(friendlyMessage(error, t('ingestReview.actionFailed'))) }
                      }}
                    /> : null}
                    <div className="flex justify-end gap-1">
                      <Button type="button" variant="ghost" size="icon" disabled={pendingAction} onClick={() => mutateRow.mutate({ id: review.id, action: 'accept' })}>
                        <Check className="size-4" />
                        <span className="sr-only">{t('ingestReview.accept')}</span>
                      </Button>
                      <Button type="button" variant="ghost" size="icon" disabled={pendingAction} onClick={() => mutateRow.mutate({ id: review.id, action: 'reject' })}>
                        <X className="size-4" />
                        <span className="sr-only">{t('ingestReview.reject')}</span>
                      </Button>
                      <Button type="button" variant="ghost" size="icon" disabled={pendingAction} onClick={() => openEdit(review)}>
                        <Pencil className="size-4" />
                        <span className="sr-only">{t('common.edit')}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
          <ChevronLeft className="size-4" />
          {t('ingestReview.prev')}
        </Button>
        <span className="nf-mono text-sm text-muted-foreground">{page}</span>
        <Button type="button" variant="outline" size="sm" disabled={!canNext} onClick={() => setPage((value) => value + 1)}>
          {t('ingestReview.next')}
          <ChevronRight className="size-4" />
        </Button>
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => {
        if (!open && !editMutation.isPending) setEditing(null)
      }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('ingestReview.editTitle', { name: editing?.canonical_name ?? '' })}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {EDIT_FIELDS.map((field) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={`ingest-edit-${field}`}>{t(`ingestReview.fields.${field}`)}</Label>
                <Input
                  id={`ingest-edit-${field}`}
                  value={editValues[field] ?? ''}
                  onChange={(event) => setEditValues((prev) => ({ ...prev, [field]: event.target.value }))}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={editMutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={() => editMutation.mutate()} disabled={editMutation.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
